import * as merge from 'deepmerge';
import EventBus from '@services/EventBus';
import AjaxError from '@services/ajax/AjaxError';
import AjaxPromiseBuilder from '@services/ajax/AjaxPromiseBuilder';
import LoadingState from '@services/loading/LoadingState';
import Form from '@form/Form';
import UrlParameterInterface from '@repositories/UrlParameterInterface';
import HttpMethodEnum from '@js/enums/HttpMethodEnum';

declare global {
  interface Window {
    moduleName: string|null;
  }
}

export default class AjaxService {
  /**
   * Gate promise used to serialize the very first requests fired while no PHPSESSID cookie exists yet (a cold
   * start, e.g. a freshly opened password-reset link with no prior browser session). See buildAjaxPromise() for
   * why this is necessary and docs/csrf-for-ajax-call.md for the full race-condition analysis.
   */
  private coldStartGate: Promise<void> | null = null;

  private readonly defaultHeaders: { [header: string]: string; } = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'X-Requested-With': 'XMLHttpRequest',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Content-Type': 'application/vnd.api+json',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Accept': 'application/vnd.api+json',
  };

  private readonly document: Document;

  private readonly eventBus: EventBus;

  /**
   * CSRF token read from the last response's X-CSRF-TOKEN header. Used as a fallback only when no CSRF-TOKEN
   * cookie can be read at all (e.g. when the application runs cross-site inside an iframe with SameSite=Lax,
   * where the browser never stores the cookie in the first place - see the DAT host definition integration).
   *
   * Deliberately NOT preferred over the cookie (see getCsrfToken()): unlike the cookie - which is always written
   * together with its matching PHPSESSID by the very same response - this in-memory value can go stale if a
   * different, concurrent request's response updates the cookie jar (and with it the session) after this value
   * was cached but before it is read again. See docs/csrf-for-ajax-call.md for the full race-condition analysis.
   */
  private inMemoryCsrfToken: string | undefined;

  public constructor(eventBus: EventBus) {
    this.eventBus = eventBus;

    this.document = document;
  }

  public addSessionIdQueryParameter(url: string): string {
    const urlParameters = new URLSearchParams(window.location.search);
    const sessionId = urlParameters.get('PHPSESSID');
    if (!sessionId) {
      return url;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return this.appendParametersToUrl(url, {PHPSESSID: sessionId});
  }

  public appendParametersToUrl(url: string, parameters: UrlParameterInterface): string {
    if (parameters.length === 0) {
      return url;
    }
    const urlInstance = new URL(url, location.href);
    for (const parameter in parameters) {
      const value = parameters[parameter];
      if (!Array.isArray(value)) {
        urlInstance.searchParams.set(parameter, '' + value)
        continue;
      }
      value.forEach((value) => {
        urlInstance.searchParams.append(parameter + '[]', '' + value)
      })
    }
    return urlInstance.toString();
  }

  public getCsrfToken(): string | undefined {
    return this.getCsrfTokenFromCookie() ?? this.inMemoryCsrfToken;
  }

  public getDropdownOptionsLegacy(url: string, urlParameters: UrlParameterInterface = {}): Promise<any> {
    url = this.appendParametersToUrl(url, urlParameters);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return this.getJsonWithHeaders(url, {'X-Csp-Disable-Version-Redirect': 'true'});
  }

  public getJson(url: string): Promise<any> {
    const fetchConfig = this.getFetchConfig();
    return this.buildAjaxPromise(url, fetchConfig);
  }

  public getJsonWithHeaders(url: string, headers: { [header: string]: string; }): Promise<any> {
    const fetchConfig = this.getFetchConfig({headers: headers});
    return this.buildAjaxPromise(url, fetchConfig);
  }

  /**
   * Get fractal with all includes specified in the include array
   * @see https://fractal.thephpleague.com/transformers/
   *
   * @param url
   * @param includes
   * @param excludes
   */
  public getJsonWithInclude(url: string, includes: string[], excludes: string[]): Promise<any> {
    const params: UrlParameterInterface = {};
    if (includes.length) {
      params['include'] = includes;
    }
    if (excludes.length) {
      params['exclude'] = excludes;
    }
    return this.getJsonWithParameters(url, params);
  }

  public getJsonWithParameters(url: string, urlParameters?: UrlParameterInterface): Promise<any> {
    const urlWithParameters = this.appendParametersToUrl(url, urlParameters ?? {});
    return this.getJson(urlWithParameters);
  }

  public patchJson(url: string, data: any): Promise<any> {
    const fetchConfig = this.getFetchConfigWithJsonData(data, 'PATCH');
    return this.buildAjaxPromise(url, fetchConfig);
  }

  public postFormData(url: string, form: Form): Promise<any> {
    return this.buildAjaxPromiseForForm(url, 'POST', form);
  }

  public postFormDataPlain(url: string, form: FormData): Promise<any> {
    const fetchConfig: any = {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
      // headers must be present (even empty) for refreshCsrfTokenHeader() to add the CSRF token in
      // dispatchAjaxPromise() - see that method's comment.
      headers: {},
    };

    return this.buildAjaxPromise(url, fetchConfig);
  }

  public postFormDataPlainAjax(url: string, form: FormData): Promise<any> {
    const fetchConfig: any = {
      method: 'POST',
      headers: {
        //  eslint-disable-next-line @typescript-eslint/naming-convention
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: form,
    };

    return this.buildAjaxPromise(url, fetchConfig);
  }

  public postJson(url: string, data: any): Promise<any> {
    const fetchConfig = this.getFetchConfigWithJsonData(data, 'POST');
    return this.buildAjaxPromise(url, fetchConfig);
  }

  public postUrlEncoded(url: string, data: Record<string, string>): Promise<any> {
    const formBody = new URLSearchParams();
    Object.keys(data).forEach((key) => {
      formBody.append(key, data[key]);
    });

    const fetchConfig = {
      method: 'POST',
      headers: {
        //  eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formBody.toString(),
    };

    return this.buildAjaxPromise(url, fetchConfig);
  }

  public putFormData(url: string, form: Form): Promise<any> {
    return this.buildAjaxPromiseForForm(url, 'PUT', form);
  }

  public putJson(url: string, data: any): Promise<any> {
    const fetchConfig = this.getFetchConfigWithJsonData(data, 'PUT');
    return this.buildAjaxPromise(url, fetchConfig);
  }

  public sendDelete(url: string): Promise<any> {
    const fetchConfig = this.getFetchConfig({
      'method': 'DELETE',
    });
    return this.buildAjaxPromise(url, fetchConfig);
  }

  /**
   * Use this when the HTTP method is dynamically determined.
   * This method does not allow supplying data for the request body.
   */
  public sendRequest(httpMethod: HttpMethodEnum, url: string): Promise<any> {
    switch (httpMethod) {
      case HttpMethodEnum.GET:
        return this.getJson(url);
      case HttpMethodEnum.POST:
        return this.postJson(url, {});
      case HttpMethodEnum.DELETE:
        return this.sendDelete(url);
      default:
        throw new Error(`Unexpected HTTP method: ${httpMethod}`);
    }
  }

  private addModuleNameToHeader(fetchConfig: any): any {
    if (!window.moduleName || window.moduleName !== 'rent') {
      // if module name is not set to be "rent" we do not add the module name to the header
      return fetchConfig;
    }

    fetchConfig.headers['X-Module-Name'] = 'rent';
    return fetchConfig;
  }

  private buildAjaxPromise(url: string, fetchConfig: any): Promise<any> {
    fetchConfig = this.addModuleNameToHeader(fetchConfig);

    url = this.addSessionIdQueryParameter(url);

    if (this.hasSessionIdentifier()) {
      // A session (cookie or, for the DAT iframe integration, an explicit PHPSESSID URL parameter) already
      // exists, so there is no cold-start race to guard against - dispatch immediately.
      return this.dispatchAjaxPromise(url, fetchConfig);
    }

    if (!this.coldStartGate) {
      // This is the pioneer request of a cold start (no PHPSESSID yet, e.g. a freshly opened password-reset
      // link with no prior browser session): it fires immediately and becomes the gate for any request fired
      // concurrently with it (e.g. another component mounting at the same time), so those wait for the
      // pioneer's response - which sets the PHPSESSID cookie - instead of racing it into creating their own,
      // independent session with its own, differently-keyed CSRF token. See docs/csrf-for-ajax-call.md for the
      // full race-condition analysis.
      const pioneerPromise = this.dispatchAjaxPromise(url, fetchConfig);
      this.coldStartGate = pioneerPromise.then(
        () => this.clearColdStartGateIfSessionStillMissing(),
        () => this.clearColdStartGateIfSessionStillMissing()
      );
      return pioneerPromise;
    }

    // Re-evaluate from scratch once the gate settles, rather than dispatching directly: if the pioneer request
    // failed before a session could be established (e.g. a network error, unrelated to CSRF), no PHPSESSID
    // cookie exists yet even after the gate resolves. Recursing lets the first request to run after that
    // become the new pioneer instead of every waiting request racing ahead independently.
    return this.coldStartGate.finally(() => this.buildAjaxPromise(url, fetchConfig));
  }

  private buildAjaxPromiseForForm(url: string, method: string, form: Form): Promise<any> {
    const fetchConfig = this.getFetchConfigWithJsonData({data: {attributes: form.getData()}}, method);
    if (form.getLabelTranslationKeyPrefix()) {
      fetchConfig.headers['X-Validation-Label-Translation-Key-Prefix'] = form.getLabelTranslationKeyPrefix();
    }
    return this.buildAjaxPromise(url, fetchConfig);
  }

  /**
   * Allows a new pioneer request to arm the cold-start gate again if the previous one settled without a
   * session ever having been established (see the "re-evaluate from scratch" comment in buildAjaxPromise()).
   */
  private clearColdStartGateIfSessionStillMissing(): void {
    if (!this.hasSessionIdentifier()) {
      this.coldStartGate = null;
    }
  }

  private dispatchAjaxPromise(url: string, fetchConfig: any): Promise<any> {
    // Refresh the CSRF header right before the actual dispatch, rather than trusting whatever the calling
    // public method computed eagerly: a request queued behind the cold-start gate (see buildAjaxPromise()) may
    // have been built at a time when no token was available yet, but a valid one exists by the time it is
    // actually sent once the gate resolves. Recomputing here also means requests never dispatch with a header
    // that has gone stale in the meantime for any other reason.
    fetchConfig = this.refreshCsrfTokenHeader(fetchConfig);

    this.startLoading(url);
    const ajaxPromiseBuilder = new AjaxPromiseBuilder(url, fetchConfig);
    ajaxPromiseBuilder.setErrorCallback((error: Error) => this.handleError(error, url));
    ajaxPromiseBuilder.setResponseCallback((response: Response) => this.updateCsrfTokenFromResponse(response));
    ajaxPromiseBuilder.setSuccessCallback((response: any) => this.handleSuccess(response, url));
    return ajaxPromiseBuilder.getPromise();
  }

  private getCookie(name: string): string | null {
    // simple cookie parse, no libs
    const cookies = this.document.cookie ? this.document.cookie.split('; ') : [];
    for (const entry of cookies) {
      const idx = entry.indexOf('=');
      if (idx < 0) continue;
      const key = entry.slice(0, idx);
      const val = entry.slice(idx + 1);
      if (key === name) {
        return decodeURIComponent(val);
      }
    }
    return null;
  }

  private getCsrfTokenFromCookie(): string | undefined {
    return this.getCookie('CSRF-TOKEN') ?? undefined;
  }

  private getFetchConfig(additionalConfig: any = {}): any {
    const defaultConfig = {
      headers: {...this.defaultHeaders},
      credentials: 'same-origin',
    };
    return merge.all([defaultConfig, additionalConfig]);
  }

  private getFetchConfigWithJsonData(data: any, method: string): any {
    return this.getFetchConfig(
      {
        method: method,
        body: JSON.stringify(data),
      }
    );
  }

  private handleError(error: Error, url: string): void {
    this.stopLoading(url);
    this.eventBus.emit(
      'ajax-error',
      new AjaxError(
        'Bei der Anfrage ist ein Fehler aufgetreten: ' + error.message,
        error
      )
    );
  }

  private handleSuccess(reponse: any, url: string): void {
    this.stopLoading(url);
    this.eventBus.emit('ajax-success', reponse);
  }

  /**
   * Whether a session already exists, either via the PHPSESSID cookie (the regular case) or via the PHPSESSID
   * URL parameter (the DAT iframe integration, see docs/sessions.md - a cross-site embedded iframe cannot
   * receive the session cookie at all, so the session id is passed as a URL parameter for that one host case
   * instead). Used by buildAjaxPromise() to decide whether the cold-start gate is needed: once either exists,
   * every following request is guaranteed to belong to the same, already-established session, so there is no
   * risk of two concurrent requests each creating their own, independent session.
   */
  private hasSessionIdentifier(): boolean {
    if (this.getCookie('PHPSESSID') !== null) {
      return true;
    }

    return new URLSearchParams(window.location.search).get('PHPSESSID') !== null;
  }

  /**
   * Sets (or overwrites) the X-CSRF-TOKEN header with a freshly read token, right before dispatch, for every
   * fetchConfig that carries a headers object. See the call site in dispatchAjaxPromise() for why this cannot
   * be trusted to have already happened at build time: a request queued behind the cold-start gate (see
   * buildAjaxPromise()) may have been built at a time when getCsrfToken() still returned undefined (no session
   * existed yet at all), so the header must still be set once a token becomes available by the time of the
   * actual dispatch.
   *
   * Deliberately unconditional (not limited to methods/fetchConfigs that previously already carried this
   * header): sending the header on every request, including GET and postUrlEncoded(), is harmless - the
   * backend's CsrfMiddleware only ever validates it for POST/PUT/PATCH/DELETE.
   */
  private refreshCsrfTokenHeader(fetchConfig: any): any {
    if (!fetchConfig.headers) {
      return fetchConfig;
    }

    const csrfToken = this.getCsrfToken();
    if (csrfToken) {
      fetchConfig.headers['X-CSRF-TOKEN'] = csrfToken;
    }

    return fetchConfig;
  }

  private startLoading(url: string): void {
    this.eventBus.emit(LoadingState.EVENT_LOADING_START, url);
  }

  private stopLoading(url: string): void {
    this.eventBus.emit(LoadingState.EVENT_LOADING_END, url);
  }

  /**
   * Caches the token from every response's X-CSRF-TOKEN header, so subsequent requests can prefer it over the
   * cookie (see inMemoryCsrfToken doc comment for why).
   */
  private updateCsrfTokenFromResponse(response: Response): void {
    const csrfToken = response.headers.get('X-CSRF-TOKEN');
    if (csrfToken) {
      this.inMemoryCsrfToken = csrfToken;
    }
  }
}
